import ModalPopup from "../commons/components/modal/ModalPopup";
import * as React from "react";
import { useState } from "react";
import Form from "../commons/components/form/Form";
import SelectInput from "../commons/components/form/input/SelectInput";
import Option from "../commons/model/Option";
import Practice from "../commons/model/Practice";
import Doctor from "../commons/model/Doctor";
import "./AssignmentModal.scss";
import InputState from "../commons/model/form/InputState";
import sfetch from "../commons/utils/super-fetch";
import AssignmentCard from "../commons/model/AssignmentCard";
import GroupSelectInput from "../commons/components/form/input/GroupSelectInput";
import { ErrorCode } from "../commons/utils/errorCode";
import {
  buildDoctorGroupOptions,
  buildDoctorTopOptions,
  buildPracticeOptions,
} from "./AssignmentDropdownBuilder";

interface AssigmentModalProps {
  onClose: () => void;
  onSave: () => void;
  canEditDoctorAssignment: boolean;
  canEditPracticeAssignment: boolean;
  assignment: AssignmentCard;
  assignableDoctors: Doctor[];
  assignablePractices: Practice[];
  assignmentUrl: string;
}

export default function AssignmentModal({
  onClose,
  onSave,
  canEditDoctorAssignment,
  canEditPracticeAssignment,
  assignment,
  assignableDoctors,
  assignablePractices,
  assignmentUrl,
}: AssigmentModalProps) {
  const [errorMessage, setErrorMessage] = useState(null);

  const save = (form: { [key: string]: InputState }) => {
    const payload = {
      doctorId: form.doctor?.value ? { value: form.doctor?.value } : null,
      practiceId: form.practice?.value ? { value: form.practice?.value } : null,
    };

    return sfetch(assignmentUrl, {
      method: "PATCH",
      body: JSON.stringify(payload),
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then(() => onSave())
      .catch((error) => {
        error.json().then((response) => {
          if (response.code == ErrorCode.DOCTOR_PRATICE_MISMATCH) {
            setErrorMessage(i18n["Lead.Assignment.DoctorPracticeMismatch"]);
          } else {
            setErrorMessage(i18n["Global.TechnicalError"]);
          }
        });
      });
  };

  const closeModal = () => {
    onClose();
    return Promise.resolve();
  };

  const doctorTopOptions = buildDoctorTopOptions(assignment.doctorUid === null);

  const doctorGroupOptions = buildDoctorGroupOptions(assignableDoctors);

  const practiceOptions: Option[] = buildPracticeOptions(
    assignablePractices,
    assignment.practiceUid === null
  );

  const displayDoctorSelector = () => {
    let initialValue = "";
    if (assignment.doctorUid !== null) {
      initialValue = assignment.doctorUid.value;
    }
    return (
      (canEditDoctorAssignment && (
        <GroupSelectInput
          topOptions={doctorTopOptions}
          groupOptions={doctorGroupOptions}
          name={"doctor"}
          label={i18n["Page.PatientManagement.Assignment.Doctor"]}
          initialValue={initialValue}
        />
      )) || <></>
    );
  };

  const displayPracticeSelector = () => {
    let initialValue = "";
    if (assignment.practiceUid !== null) {
      initialValue = assignment.practiceUid.value;
    }
    return (
      (canEditPracticeAssignment && (
        <SelectInput
          options={practiceOptions}
          name={"practice"}
          label={i18n["Page.PatientManagement.Assignment.Practice"]}
          initialValue={initialValue}
        />
      )) || <></>
    );
  };

  return (
    <ModalPopup closeModal={closeModal}>
      <div className="title">{i18n["Page.PatientManagement.Assignment.Title"]}</div>
      <Form
        submitLabel={i18n["Page.PatientManagement.Assignment.Action.Save"]}
        submit={save}
        secondaryActionLabel={i18n["Page.PatientManagement.Assignment.Action.Cancel"]}
        secondaryAction={() => closeModal()}
        className={"assignment-modal"}
        generalErrorMsg={errorMessage}
      >
        {displayDoctorSelector()}
        {displayPracticeSelector()}
      </Form>
    </ModalPopup>
  );
}
